import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Alert
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';

interface Company {
  id: string;
  company_name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string;
  email: string;
  website?: string;
  ein?: string;
  ssn?: string;
  entity_type?: string;
  entity_type_display?: string;
  year_of_formation?: number | string;
  full_address?: string;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  updated_by_name?: string;
}

interface CompanyManagementProps {
  showLoader: boolean;
  setShowLoader: (value: boolean) => void;
}

// Countries list
const COUNTRIES = [
  'United States',
  'Canada',
  'United Kingdom',
  'Australia',
  'Germany',
  'France',
  'India',
  'China',
  'Japan',
  'Mexico',
  'Brazil',
  'Other'
];

// US States
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

// Canadian Provinces
const CANADIAN_PROVINCES = [
  'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
  'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
  'Quebec', 'Saskatchewan', 'Yukon'
];

// Get states based on country
const getStatesForCountry = (country: string): string[] => {
  if (country === 'United States') return US_STATES;
  if (country === 'Canada') return CANADIAN_PROVINCES;
  return []; // For other countries, allow free text
};

// Helper functions for formatting and masking (display only)
const formatPhoneForDisplay = (value: string): string => {
  if (!value) return '';
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
};

const formatEINForDisplay = (value: string): string => {
  if (!value) return '';
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 2) return numbers;
  return `${numbers.slice(0, 2)}-${numbers.slice(2, 9)}`;
};

const formatSSNForDisplay = (value: string): string => {
  if (!value) return '';
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 9)}`;
};

const maskEIN = (ein: string): string => {
  if (!ein) return '';
  const numbers = ein.replace(/\D/g, '');
  if (numbers.length < 9) return formatEINForDisplay(ein);
  // Show only last 4 digits: **-***5678
  return `**-***${numbers.slice(-4)}`;
};

const maskSSN = (ssn: string): string => {
  if (!ssn) return '';
  const numbers = ssn.replace(/\D/g, '');
  if (numbers.length < 9) return formatSSNForDisplay(ssn);
  // Show only last 4 digits: ***-**-1234
  return `***-**-${ssn.slice(-4)}`;
};

const CompanyManagement: React.FC<CompanyManagementProps> = ({ showLoader, setShowLoader }) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [alertMsg, setAlertMsg] = useState<{ open: boolean; type: string; content: string }>({
    open: false,
    type: 'success',
    content: ''
  });
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<string | null>(null);

  // Generate year options (current year to 100 years back)
  const generateYearOptions = (): number[] => {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let i = currentYear; i >= currentYear - 100; i--) {
      years.push(i);
    }
    return years;
  };

  const validationSchema = Yup.object({
    company_name: Yup.string().required('Company name is required'),
    address_line1: Yup.string().required('Address Line 1 is required'),
    address_line2: Yup.string().nullable(),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zipcode: Yup.string()
      .required('Zipcode is required')
      .min(3, 'Zipcode must be at least 3 characters')
      .max(10, 'Zipcode must be at most 10 characters'),
    country: Yup.string().required('Country is required'),
    phone: Yup.string()
      .required('Phone is required')
      .test('phone-length', 'Phone must be 10 digits', (value) => {
        if (!value) return false;
        const digits = value.replace(/\D/g, '');
        return digits.length === 10;
      }),
    email: Yup.string().email('Invalid email').required('Email is required'),
    website: Yup.string().url('Invalid URL format').nullable(),
    ein: Yup.string()
      .test('ein-length', 'EIN must be 9 digits', (value) => {
        if (!value) return true;
        const digits = value.replace(/\D/g, '');
        return digits.length === 0 || digits.length === 9;
      })
      .nullable(),
    ssn: Yup.string()
      .test('ssn-length', 'SSN must be 9 digits', (value) => {
        if (!value) return true;
        const digits = value.replace(/\D/g, '');
        return digits.length === 0 || digits.length === 9;
      })
      .nullable(),
    entity_type: Yup.string().nullable(),
    year_of_formation: Yup.number()
      .min(1800, 'Year must be after 1800')
      .max(new Date().getFullYear(), `Year cannot be in the future`)
      .nullable()
  });

  const formik = useFormik({
    initialValues: {
      company_name: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zipcode: '',
      country: 'United States',
      phone: '',
      email: '',
      website: '',
      ein: '',
      ssn: '',
      entity_type: '',
      year_of_formation: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setShowLoader(true);
        
        // Strip formatting - store only plain numbers
        const cleanedValues = {
          ...values,
          phone: values.phone.replace(/\D/g, ''),
          ein: values.ein ? values.ein.replace(/\D/g, '') : '',
          ssn: values.ssn ? values.ssn.replace(/\D/g, '') : '',
          website: values.website || null,
          entity_type: values.entity_type || null,
          year_of_formation: values.year_of_formation ? parseInt(values.year_of_formation.toString()) : null
        };
      
      if (editMode && selectedCompany) {
        // Edit company
        const response = await axios.put(
          `/accounts/companies/${selectedCompany.id}/edit/`,
          cleanedValues,
          { withCredentials: true }
        );
        if (response.status === 'success') {
          setAlertMsg({ open: true, type: 'success', content: 'Company updated successfully!' });
          fetchCompanies();
          setModalOpen(false);
        }
      } else {
        // Add new company
        const response = await axios.post('/accounts/companies/add/', cleanedValues, {
          withCredentials: true
        });
        if (response.status === 'success') {
          setAlertMsg({ open: true, type: 'success', content: 'Company added successfully!' });
          fetchCompanies();
          setModalOpen(false);
        }
      }
      setShowLoader(false);
      setTimeout(() => setAlertMsg({ ...alertMsg, open: false }), 3000);
    } catch (error: any) {
      setShowLoader(false);
      if (error.response) {
        if ([401, 422].includes(error.response.status)) {
          setAlertMsg({
            open: true,
            type: 'error',
            content: error.response.data.error || 'An error occurred'
          });
        } else if (error.response.status === 403) {
          setAlertMsg({ open: true, type: 'error', content: 'Session expired!' });
          setTimeout(() => (window.location.href = '/logout'), 2000);
        } else {
          setAlertMsg({ open: true, type: 'error', content: 'An error occurred' });
        }
      } else {
        setAlertMsg({ open: true, type: 'error', content: 'Network error' });
      }
      setTimeout(() => setAlertMsg({ ...alertMsg, open: false }), 3000);
    }
    }
  });

  const fetchCompanies = async () => {
    try {
      setShowLoader(true);
      const response = await axios.get('/accounts/companies/', { withCredentials: true });
      if (response.status === 'success') {
        setCompanies(response.companies);
      }
      setShowLoader(false);
    } catch (error: any) {
      setShowLoader(false);
      console.error('Error fetching companies:', error);
      if (error.response && error.response.status === 403) {
        setTimeout(() => (window.location.href = '/logout'), 2000);
      }
    }
  };

  const handleAddCompany = () => {
    setEditMode(false);
    setSelectedCompany(null);
    formik.resetForm();
    setModalOpen(true);
  };

  const handleEditCompany = (company: Company) => {
    setEditMode(true);
    setSelectedCompany(company);
    formik.setValues({
      company_name: company.company_name,
      address_line1: company.address_line1,
      address_line2: company.address_line2 || '',
      city: company.city,
      state: company.state,
      zipcode: company.zipcode,
      country: company.country,
      phone: formatPhoneForDisplay(company.phone),
      email: company.email,
      website: company.website || '',
      ein: formatEINForDisplay(company.ein || ''),
      ssn: formatSSNForDisplay(company.ssn || ''),
      entity_type: company.entity_type || '',
      year_of_formation: company.year_of_formation ? company.year_of_formation.toString() : ''
    });
    setModalOpen(true);
  };

  const handleDeleteCompany = async () => {
    if (!companyToDelete) return;
    
    try {
      setShowLoader(true);
      const response = await axios.delete(`/accounts/companies/${companyToDelete}/delete/`, {
        withCredentials: true
      });
      if (response.status === 'success') {
        setAlertMsg({ open: true, type: 'success', content: 'Company deleted successfully!' });
        fetchCompanies();
        setDeleteConfirmModal(false);
        setCompanyToDelete(null);
      }
      setShowLoader(false);
      setTimeout(() => setAlertMsg({ ...alertMsg, open: false }), 3000);
    } catch (error: any) {
      setShowLoader(false);
      if (error.response) {
        if ([401, 422].includes(error.response.status)) {
          setAlertMsg({
            open: true,
            type: 'error',
            content: error.response.data.error || 'An error occurred'
          });
        } else if (error.response.status === 403) {
          setAlertMsg({ open: true, type: 'error', content: 'Session expired!' });
          setTimeout(() => (window.location.href = '/logout'), 2000);
        } else {
          setAlertMsg({ open: true, type: 'error', content: 'An error occurred' });
        }
      } else {
        setAlertMsg({ open: true, type: 'error', content: 'Network error' });
      }
      setTimeout(() => setAlertMsg({ ...alertMsg, open: false }), 3000);
    }
  };

  const confirmDelete = (companyId: string) => {
    setCompanyToDelete(companyId);
    setDeleteConfirmModal(true);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  return (
    <>
      {alertMsg.open && (
        <Alert
          color={alertMsg.type === 'success' ? 'success' : 'danger'}
          toggle={() => setAlertMsg({ ...alertMsg, open: false })}
          style={{ fontWeight: 'bold' }}
          fade={false}
        >
          {alertMsg.content}
        </Alert>
      )}

      <Row className="border-bottom mb-4">
        <Col md={3}>
          <p className="mb-0 font-size-16 text-dark fw-medium">Company Details</p>
          <p>Manage your company information here.</p>
        </Col>
        <Col md={9}>
          <Card className="rounded-4 border">
            <CardBody className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">My Companies</h5>
                <Button color="primary" size="sm" onClick={handleAddCompany}>
                  <i className="bx bx-plus me-1"></i> Add Company
                </Button>
              </div>

              {companies.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bx bx-buildings font-size-24 text-muted mb-3 d-block"></i>
                  <p className="text-muted mb-0">No companies added yet.</p>
                  <p className="text-muted">Click "Add Company" to get started.</p>
                </div>
              ) : (
                <Row>
                  {companies.map((company) => (
                    <Col md={6} key={company.id} className="mb-3">
                      <Card className="border h-100 shadow-sm">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className="flex-grow-1">
                              <h5 className="mb-1 text-dark">
                                <i className="bx bx-buildings me-2 text-primary"></i>
                                {company.company_name}
                              </h5>
                              {company.year_of_formation && (
                                <span className="badge bg-soft-primary text-primary font-size-11">
                                  Est. {company.year_of_formation}
                                </span>
                              )}
                            </div>
                            <div className="d-flex gap-2">
                              <button
                                className="btn btn-sm btn-soft-info"
                                onClick={() => handleEditCompany(company)}
                                title="Edit Company"
                              >
                                <i className="bx bx-edit-alt"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-soft-danger"
                                onClick={() => {
                                  setCompanyToDelete(company.id);
                                  setDeleteConfirmModal(true);
                                }}
                                title="Delete Company"
                              >
                                <i className="bx bx-trash"></i>
                              </button>
                            </div>
                          </div>

                          <div className="company-details">
                            <div className="mb-2">
                              <i className="bx bx-envelope me-2 text-muted"></i>
                              <a href={`mailto:${company.email}`} className="text-body">
                                {company.email}
                              </a>
                            </div>
                            <div className="mb-2">
                              <i className="bx bx-phone me-2 text-muted"></i>
                              <a href={`tel:${company.phone}`} className="text-body">
                                {formatPhoneForDisplay(company.phone)}
                              </a>
                            </div>
                            <div className="mb-2">
                              <i className="bx bx-map me-2 text-muted"></i>
                              <span className="text-muted">{company.full_address}</span>
                            </div>
                            {company.website && (
                              <div className="mb-2">
                                <i className="bx bx-globe me-2 text-muted"></i>
                                <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-body">
                                  {company.website}
                                </a>
                              </div>
                            )}
                            {(company.ein || company.ssn || company.entity_type_display) && (
                              <div className="mt-3 pt-3 border-top">
                                {company.entity_type_display && (
                                  <div className="d-inline-block me-3 mb-2">
                                    <small className="text-muted d-block">Company Type</small>
                                    <span className="font-size-13">{company.entity_type_display}</span>
                                  </div>
                                )}
                                {company.ein && (
                                  <div className="d-inline-block me-3 mb-2">
                                    <small className="text-muted d-block">EIN</small>
                                    <span className="font-size-13 font-monospace">{maskEIN(company.ein)}</span>
                                  </div>
                                )}
                                {company.ssn && (
                                  <div className="d-inline-block mb-2">
                                    <small className="text-muted d-block">SSN</small>
                                    <span className="font-size-13 font-monospace">{maskSSN(company.ssn)}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="mt-3 pt-2 border-top">
                            <small className="text-muted">
                              <i className="bx bx-time-five me-1"></i>
                              Updated {new Date(company.updated_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </small>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Company Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg">
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
          {editMode ? 'Edit Company' : 'Add New Company'}
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="company_name">
                    Company Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="company_name"
                    name="company_name"
                    value={formik.values.company_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.company_name && !!formik.errors.company_name}
                  />
                  <FormFeedback>{formik.errors.company_name}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="email">
                    Email <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.email && !!formik.errors.email}
                  />
                  <FormFeedback>{formik.errors.email}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label htmlFor="address_line1">
                    Address Line 1 <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="address_line1"
                    name="address_line1"
                    placeholder="Street address, P.O. box"
                    value={formik.values.address_line1}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.address_line1 && !!formik.errors.address_line1}
                  />
                  <FormFeedback>{formik.errors.address_line1}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
                  <Input
                    type="text"
                    id="address_line2"
                    name="address_line2"
                    placeholder="Apartment, suite, unit, building, floor, etc."
                    value={formik.values.address_line2}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="city">
                    City <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="city"
                    name="city"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.city && !!formik.errors.city}
                  />
                  <FormFeedback>{formik.errors.city}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="state">
                    State/Province <span className="text-danger">*</span>
                  </Label>
                  {getStatesForCountry(formik.values.country).length > 0 ? (
                    <Input
                      type="select"
                      id="state"
                      name="state"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.state && !!formik.errors.state}
                    >
                      <option value="">Select State/Province</option>
                      {getStatesForCountry(formik.values.country).map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </Input>
                  ) : (
                    <Input
                      type="text"
                      id="state"
                      name="state"
                      placeholder="Enter state/province"
                      value={formik.values.state}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.state && !!formik.errors.state}
                    />
                  )}
                  <FormFeedback>{formik.errors.state}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="country">
                    Country <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="select"
                    id="country"
                    name="country"
                    value={formik.values.country}
                    onChange={(e) => {
                      formik.setFieldValue('country', e.target.value);
                      // Reset state when country changes
                      formik.setFieldValue('state', '');
                    }}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.country && !!formik.errors.country}
                  >
                    <option value="">Select Country</option>
                    {COUNTRIES.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{formik.errors.country}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="zipcode">
                    Zipcode <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="zipcode"
                    name="zipcode"
                    placeholder="e.g., 12345 or 12345-6789"
                    value={formik.values.zipcode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.zipcode && !!formik.errors.zipcode}
                    maxLength={10}
                  />
                  <FormFeedback>{formik.errors.zipcode}</FormFeedback>
                  <small className="text-muted">Up to 10 characters (e.g., 12345 or 12345-6789)</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="phone">
                    Phone <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    id="phone"
                    name="phone"
                    placeholder="(XXX) XXX-XXXX"
                    value={formik.values.phone}
                    onChange={(e) => {
                      const formatted = formatPhoneForDisplay(e.target.value);
                      formik.setFieldValue('phone', formatted);
                    }}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.phone && !!formik.errors.phone}
                    maxLength={14}
                  />
                  <FormFeedback>{formik.errors.phone}</FormFeedback>
                  <small className="text-muted">Format: (XXX) XXX-XXXX (e.g., (555) 123-4567)</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="website">Website (Optional)</Label>
                  <Input
                    type="url"
                    id="website"
                    name="website"
                    placeholder="https://www.example.com"
                    value={formik.values.website}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.website && !!formik.errors.website}
                  />
                  <FormFeedback>{formik.errors.website}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="entity_type">Company Type (Optional)</Label>
                  <Input
                    type="select"
                    id="entity_type"
                    name="entity_type"
                    value={formik.values.entity_type}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <option value="">Select Company Type</option>
                    <option value="sole_proprietorship">Sole Proprietorship</option>
                    <option value="partnership">Partnership</option>
                    <option value="llc">LLC</option>
                    <option value="corporation">Corporation</option>
                    <option value="s_corp">S Corporation</option>
                    <option value="non_profit">Non-Profit</option>
                    <option value="other">Other</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="year_of_formation">
                    Year of Incorporation/Formation
                  </Label>
                  <Input
                    type="select"
                    id="year_of_formation"
                    name="year_of_formation"
                    value={formik.values.year_of_formation}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.year_of_formation && !!formik.errors.year_of_formation}
                  >
                    <option value="">Select Year</option>
                    {generateYearOptions().map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </Input>
                  <FormFeedback>{formik.errors.year_of_formation}</FormFeedback>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="ein">EIN (Optional)</Label>
                  <Input
                    type="text"
                    id="ein"
                    name="ein"
                    placeholder="XX-XXXXXXX"
                    value={formik.values.ein}
                    onChange={(e) => {
                      const formatted = formatEINForDisplay(e.target.value);
                      formik.setFieldValue('ein', formatted);
                    }}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.ein && !!formik.errors.ein}
                    maxLength={10}
                  />
                  <FormFeedback>{formik.errors.ein}</FormFeedback>
                  <small className="text-muted">Format: XX-XXXXXXX (e.g., 12-3456789)</small>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label htmlFor="ssn">SSN (Optional)</Label>
                  <Input
                    type="text"
                    id="ssn"
                    name="ssn"
                    placeholder="XXX-XX-XXXX"
                    value={formik.values.ssn}
                    onChange={(e) => {
                      const formatted = formatSSNForDisplay(e.target.value);
                      formik.setFieldValue('ssn', formatted);
                    }}
                    onBlur={formik.handleBlur}
                    invalid={formik.touched.ssn && !!formik.errors.ssn}
                    maxLength={11}
                  />
                  <FormFeedback>{formik.errors.ssn}</FormFeedback>
                  <small className="text-muted">Format: XXX-XX-XXXX (e.g., 123-45-6789)</small>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button color="primary" onClick={() => formik.handleSubmit()}>
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteConfirmModal} toggle={() => setDeleteConfirmModal(!deleteConfirmModal)}>
        <ModalHeader toggle={() => setDeleteConfirmModal(!deleteConfirmModal)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>Are you sure you want to delete this company?</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteConfirmModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteCompany}>
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CompanyManagement;

